/**
 * The attributed estate's half of a work's public view — OND-172.
 *
 * THE ONLY PANEL ON THIS RECORD THAT IS NOT THE OWNER'S, and that is the whole
 * reason it is a file of its own rather than a row on `WorkVisibilityPanel`.
 * OND-172 divides a public view between two parties along a line that does not
 * overlap: the artist, the year and whether the attribution is contested are the
 * estate's; the title, the medium, the photograph, the location and whether the
 * work is shown at all are the owner's. "Neither can overrule the other on what
 * is properly theirs, so there is no precedence rule to write."
 *
 * So the two panels are never on one screen. The server answers
 * `attributionControls` null for everybody but the attributed estate and
 * `visibility` null for everybody but the owner, so each party sees one panel and
 * neither is shown a control the other owns — which is a stronger statement than
 * a disabled switch, and the only one that matches the SQL.
 *
 * ONE SWITCH AND TWO SENTENCES, the second of which is NOT a control. Whether a
 * public view marks the attribution as contested is derived from the work's
 * history — an estate disputes the authorship claim, in its own name — and
 * nothing stores a verdict anywhere. The sentence is on the panel so that the
 * absence of a switch for it reads as a design rather than as something missing.
 *
 * It renders nothing at all for anybody else, because the controller hands it
 * null.
 */
import { WORK_ATTRIBUTION_VISIBILITY_COPY } from "./work.constants.ts";
import type { AttributionVisibilityPanel } from "./work.model.ts";

import "./work.css";

type Props = {
  panel: AttributionVisibilityPanel | null;
  busy: boolean;
  failed: boolean;
  onChoose: (attributionPublic: boolean) => void;
};

export function WorkAttributionVisibilityPanel({
  panel,
  busy,
  failed,
  onChoose,
}: Props) {
  if (panel === null) return null;

  return (
    <section
      className="work__panel work-estate"
      aria-labelledby="work-attribution-reach-heading"
    >
      <h3 className="work__panel-head" id="work-attribution-reach-heading">
        {WORK_ATTRIBUTION_VISIBILITY_COPY.heading}
      </h3>
      {/* The lead says what this panel does NOT control, which matters more here
          than on the owner's: an estate looking for the switch that takes a work
          off a public page should be told there is none rather than hunt for it. */}
      <p className="work-estate__lead">{WORK_ATTRIBUTION_VISIBILITY_COPY.lead}</p>

      {/* WRITTEN POSITIVELY — 'Show the artist and the year', checked by default —
          like the identity switch on the owner's estate panel and for its reason:
          published is the product default, so the off position has to be the
          thing somebody turned off. Unlike that one, no inversion happens here,
          because the server stores the positive form. */}
      <label className="work-estate__row">
        <input
          type="checkbox"
          checked={panel.on}
          disabled={busy}
          onChange={(event) => onChoose(event.target.checked)}
        />
        <span className="work-estate__row-body">
          <span className="work-estate__row-title">{panel.label}</span>
          <span className="work-estate__row-note">{panel.state}</span>
        </span>
      </label>

      {/* The contested mark, as a statement and never as a control. Which of its
          three sentences to print is the model's; this renders one of them. */}
      <p className="work-estate__lead">{panel.contested}</p>

      {busy && (
        <p className="work-estate__state" role="status">
          {WORK_ATTRIBUTION_VISIBILITY_COPY.saving}
        </p>
      )}
      {failed && !busy && (
        <p className="work-estate__state work-estate__state--failed" role="alert">
          {WORK_ATTRIBUTION_VISIBILITY_COPY.failed}
        </p>
      )}
    </section>
  );
}
